/* Core & libraries */
import React, {useEffect, useState} from "react";
import { FormattedMessage } from "react-intl";

/* Helpers, constants, custom methods, internal libraries */
import { useGenericApiFetch }  from "../endpoints/base";
import { useGlobalUpdateContext } from "../contexts/globalContext";
import { useGlobalContext } from "../contexts/globalContext";
import {
  ADD_DOMAIN_ENDPOINT,
  COMPANY_DETAILS_ENDPOINT,
  GET_ACTIVE_COMPANIES_ENDPOINT,
  GET_DOMAINS_ENDPOINT,
  GET_REMOVED_COMPANIES_ENDPOINT,
  REMOVE_DOMAIN_ENDPOINT,
  SEARCH_COMPANIES_ENDPOINT,
  GET_COMPANY_ADMINS_ENDPOINT,
  COMPANY_DELETE_ENDPOINT,
  RESEND_DOMAIN_VERIFICATION_ENDPOINT,
  GET_COMPANY_INFO_BANNER,
  GET_COMPANY_ACTIVITY_FEED,
  SET_OVERRIDE_THRESHOLD,
  UPDATE_COMPAMY_CITIPCAN_ENDPOINT,
  GET_BOOKING_FIELDS_ENDPOINT,
  ADD_EDIT_BOOKING_FIELD_ENDPOINT,
  DELETE_BOOKING_FIELD_ENDPOINT,
  ADMIN_OVERRIDE_DISCOUNT_ENDPOINT,
  GET_OPTIONS_ENDPOINT,
  GET_PENDING_COMPANIES_ENDPOINT,
  GRID_ROWS_COUNT_ENDPOINT,
  DISMISS_TRIP_CREDITS_BANNER
} from "../endpoints/endpoints";
import { useNotificationContext } from "../contexts/notificationContext";
import {roles} from "../constants/appConstants"
import { useUserContext } from "../contexts/userContext";

/* Components */
/* Styles */

export const useCompaniesService = () => {
  const [companiesResponse, setCompaniesResponse] = useState({})
  const [activityResponse, setActivityResponse] = useState([])
  const [activityLoading, setActivityLoading] = useState(true)
  const [activityFailed, setActivityFailed] = useState(false)
  const { genericApiFetch } = useGenericApiFetch();
  const { setNotification } = useNotificationContext();
  const globalData = useGlobalContext();
  const setGlobalData = useGlobalUpdateContext();
  const userDetails = useUserContext();
  const providerCode = userDetails?.userInfo?.providerInfo?.providerCode;

  const companiesGridSpinner = {
    ACTIVE: "activeCompaniesGridSpinner",
    REMOVED: "removedCompaniesGridSpinner",
    PENDING: "pendingCompaniesGridSpinner",
  };

  const getUrl = (type) => {
    switch (type) {
      case "ACTIVE":
        return GET_ACTIVE_COMPANIES_ENDPOINT;
      case "REMOVED":
        return GET_REMOVED_COMPANIES_ENDPOINT;
      case "PENDING":
        return GET_PENDING_COMPANIES_ENDPOINT;
      default:
        return "";
    }
  }

  const getCompaniesList = async (options, type) => {
    if (options) {

      let filters = "";
      if (options.filters) {
        for (const [key, value] of Object.entries(options.filters)) {
          value.map(item => {
            if (item) {
              filters += `${filters ? "," : ""}${key}-${item}`
            }
          })
        }
      }
      options = {
        ...options,
        filters
      }

      const response = await genericApiFetch({
        url: getUrl(type),
        method: "GET",
        queryParams: options,
        spinner: companiesGridSpinner[type]
      });

      if (response.success) {
        setCompaniesResponse(response.data);
      } else {
        setCompaniesResponse({ results: null });
      }
    }
  };

  const searchCompanies = async (searchTerm, searchType, page = 1) => {
    const reqBody = {
      searchTerm: searchTerm,
      status: searchType,
      page: page
    }
    const response = await genericApiFetch({
      url: SEARCH_COMPANIES_ENDPOINT,
      method: "POST",
      data : reqBody,
      spinner: "searchGridFilters"
    });

    if (response.success) {
      return {
        companies: response?.data?.companies ?? [],
        resultTotal: response?.data?.resultTotal ?? 0,
      };
    }
    return false;
  }

  const getCompanyBanner = async (companyEntityId) => {
    const response = await genericApiFetch({
      url: GET_COMPANY_INFO_BANNER.replace("{providerCode}", providerCode?.toLowerCase()).replace("{companyEntityId}", companyEntityId),
      method: "GET",
    });

    if (response.success) {
      return response.data;
    }
    return false;
  }

  const getCompanyDetails = async (companyEntityId) => {
    const response = await genericApiFetch({
      url: COMPANY_DETAILS_ENDPOINT.replace("{providerCode}", providerCode?.toLowerCase()).replace("{companyId}", companyEntityId),
      method: "GET",
    });

    if (response.success) {
      return response.data;
    }
    return false;
  }

  const updateCompany = async (payload) => {
    const {companyId, ...data} = payload;

    const response = await  genericApiFetch({
      url: COMPANY_DETAILS_ENDPOINT.replace("{providerCode}", providerCode?.toLowerCase()).replace("{companyId}", companyId),
      method: "PUT",
      data: data,
      spinner: "updateCompanySpinner"
    })
    if (response.success) {
      if(response.data?.errors?.some((e) => e.errorCode === "1000")) {
        setNotification(
          "error",
          <FormattedMessage id="notifications.updateCompany.alreadyExists" />,
          <FormattedMessage id="notifications.updateCompany.alreadyExistsDescription" />,
          true,
          50000
        );

        return false
      }

      const hasInvalidPromoCodes = response.data?.errors?.some(
        (e) => e.errorCode === "AA201" && e.errorDescription
      );
      setNotification(
        "success",
        <FormattedMessage id="notification.updateCompanySuccessTitle" />,
        <FormattedMessage id="notification.updateCompanySuccessText" />,
        false,
        hasInvalidPromoCodes ? 999999 : undefined
      )

      setGlobalData({
        ...globalData,
        showIncompleteBanner: false
      })
      return response.data || true
    }

    setNotification(
      "error",
      <FormattedMessage id="notifications.updateCompanyErrorTitle" />,
      <FormattedMessage id="notifications.updateCompanyErrorText" />
    );

    if (response.success === false && response.errors) {
      return response;
    }

    return false
  }

  const deleteCompany = async (payload) => {

    const response = await genericApiFetch({
      url: COMPANY_DELETE_ENDPOINT.replace("{providerCode}", providerCode?.toLowerCase()).replace("{companyId}", payload.companyId),
      method: "DELETE",
      spinner: "deleteCompanyActionSpinner",
    });
    if (response.success) {
      setNotification(
        "success",
        <FormattedMessage id="notification.deleteCompanySuccessTitle" />,
        <FormattedMessage id="notification.deleteCompanySuccessText" values={{name: `${payload?.companyName}`}}/>
      )
      return true;
    }
    return false;
  }

  const getCompanyAdmins = async (companyEntityId) => {
    const response = await genericApiFetch({
      url: GET_COMPANY_ADMINS_ENDPOINT.replace("{companyEntityId}", companyEntityId).replace("{roleId}", roles.travelManegerRole),
      method: "GET",
    });
    if (response.success) {
      return response.data;
    }
    return false;
  }

  const setOverrideThreshold = async (companyEntityId, overrideThreshold) => {
    const reqBody = { 
      overrideThreshold: overrideThreshold,
    }

    const response = await genericApiFetch({
      url: SET_OVERRIDE_THRESHOLD.replace("{companyEntityId}", companyEntityId),
      method: "POST",
      spinner: overrideThreshold ? "overrideThresholdSpinner" : "revertThresholdSpinner",
      data : reqBody,
    });
    if (response.success) {
      setNotification(
        "success",
        <FormattedMessage id="notifications.overrideRevertThresholdSuccessTitle" />,
        <FormattedMessage id={overrideThreshold ? "notifications.overrideThresholdSuccessText" : "notifications.revertThresholdSuccessText" } />
      )
      return true;
    }
    setNotification(
      "error",
      <FormattedMessage id="notifications.overrideRevertThresholdErrorTitle" />,
      <FormattedMessage id="notifications.overrideRevertThresholdErrorText" />
    )
    return false;
  }

  const getDomains = async (companyEntityId) => {
    const response = await genericApiFetch({
      url: GET_DOMAINS_ENDPOINT.replace("{companyEntityId}", companyEntityId),
      method: "GET",
    });

    if (response.success) {
      return response.data;
    }
    setNotification(
      "error",
      <FormattedMessage id="notifications.getDomainsErrorTitle" />,
      <FormattedMessage id="notifications.getDomainsErrorText" />
    )
    return false;
  }

  const resendVerification = async (payload) => {
    const response = await genericApiFetch({
      url: RESEND_DOMAIN_VERIFICATION_ENDPOINT.replace("{companyEntityId}", payload.companyEntityId),
      method: "POST",
      data: {
        domainId: payload.domainId
      }
    });
    if (response.success) {
      setNotification(
        "success",
        <FormattedMessage id="notification.resendDomainVerificationSuccessTitle" />,
        <FormattedMessage id="notification.resendDomainVerificationSuccessText" />,
        true,
        8000
      )
      return true;
    }
    setNotification(
      "error",
      <FormattedMessage id="notifications.resendDomainVerificationErrorTitle" />,
      <FormattedMessage id="notifications.resendDomainVerificationErrorText" />
    )
    return false;
  }

  const removeDomain = async (payload) => {
    const response = await genericApiFetch({
      url: REMOVE_DOMAIN_ENDPOINT.replace("{companyEntityId}", payload.companyEntityId).replace("{domainId}", payload.domainId),
      method: "DELETE",
      spinner: "deleteActionSpinner"
    });
    if (response.success) {
      setNotification(
        "success",
        <FormattedMessage id="notification.removeDomainVerificationSuccessTitle" />,
        <FormattedMessage id="notification.removeDomainVerificationSuccessText" values={{domain: `${payload?.domain}`}}/>
      )
      return true;
    }
    setNotification(
      "error",
      <FormattedMessage id="notifications.removeDomainVerificationErrorTitle" />,
      <FormattedMessage id="notifications.removeDomainVerificationErrorText" />
    )
    return false;
  }

  const addDomain = async (payload) => {
    const {companyEntityId, ...data} = payload;
    const response = await genericApiFetch({
      url: ADD_DOMAIN_ENDPOINT.replace("{companyEntityId}", companyEntityId),
      method: "POST",
      data : data,
      spinner: "addDomainSpinner"
    });
    if (response.success) {
      return true;
    } else if(!response.success && response.errorMessages?.[0]?.code === "2013") {
      setNotification(
        "error",
        <FormattedMessage id="notifications.addDomainDuplicateErrorTitle" />,
        <FormattedMessage id="notifications.addDomainDuplicateErrorText"/>
      )
    } else if(!response.success && response.errorMessages?.[0]?.code === "2008") {
      setNotification(
        "error",
        <FormattedMessage id="notifications.addDomainUnsupportedErrorTitle" />,
        <FormattedMessage id="notifications.addDomainUnsupportedErrorText"/>
      )
    } else {
      setNotification(
        "error",
        <FormattedMessage id="notifications.addDomainVerificationErrorTitle" />,
        <FormattedMessage id="notifications.addDomainVerificationErrorText"/>
      )
    }
    return false;
  }

  const getCompanyActivity = async (companyEntityId, options) => {
    const response = await genericApiFetch({
      url: GET_COMPANY_ACTIVITY_FEED.replace("{companyEntityId}", companyEntityId),
      queryParams: options,
      method: "GET",
    });

    if (response.success) {  
        setActivityResponse((prevActivities) => [
          ...prevActivities,
          ...response?.data?.activityLogPage
          ,
        ]);
      setActivityLoading(false);
      return response?.data?.activityLogPage.length > 0;
    } else {
      setActivityLoading(false);
      setActivityFailed(true);
      return false;
    }
  }

  const updateCompanyCitiPCAN = async (payload, companyEntityId) => {
    const response = await  genericApiFetch({
      url: UPDATE_COMPAMY_CITIPCAN_ENDPOINT.replace("{providerCode}", providerCode?.toLowerCase()).replace("{companyEntityId}", companyEntityId),
      method: "PUT",
      data: payload,
      spinner: "updateCompanyPCANSpinner"
    })
    if (response.success) {
      if (response.data.status === "SUCCESS") {
        setNotification(
          "success",
          <FormattedMessage id="notifications.editUserPCANsSuccesTitle"/>,
          <FormattedMessage id="notifications.editUserPCANsSuccesText"/>
        )
      }
      return response.data
    }
    setNotification(
      "error",
      <FormattedMessage id="notifications.editUserPCANsErrorTitle" />,
      <FormattedMessage id="notifications.editUserPCANsErrorErrorText" />
    )
    return false
  };

  const getBookingFields = async (companyEntityId) => {
    const response = await genericApiFetch({
      url: GET_BOOKING_FIELDS_ENDPOINT.replace("{companyId}", companyEntityId),
      method: "GET",
    });
    if (response.success) {
      return response.data;
    }
    setNotification(
      "error",
      <FormattedMessage id="notifications.bookingFields.errorTitle" />,
      <FormattedMessage id="notifications.bookingFields.errorText" />
    )
    return false;
  }

  const addEditBookingField = async (companyEntityId, payload) => {
    const response = await genericApiFetch({
      url: ADD_EDIT_BOOKING_FIELD_ENDPOINT.replace("{companyId}", companyEntityId),
      method: "PUT",
      data: payload,
      spinner: "addEditBookingFieldSpinner",
    });
    if (response.success) {
      if (payload.hasOwnProperty("bookingFieldId")) {
        setNotification(
          "success",
          <FormattedMessage id="notifications.bookingFields.edit.successTitle" />,
          <FormattedMessage id="notifications.bookingFields.edit.successText" />
        );
      } else {
        setNotification(
          "success",
          <FormattedMessage id="notifications.bookingFields.add.successTitle" />,
          <FormattedMessage id="notifications.bookingFields.add.successText" />
        );
      }
      return true;
    } else {
      setNotification(
        "error",
        <FormattedMessage id="notifications.bookingFields.errorTitle" />,
        <FormattedMessage id="notifications.bookingFields.errorText" />
      );
      return false;
    }
  }

  const removeBookingField = async (companyEntityId, bookingFieldId) => {
    const response = await genericApiFetch({
      url: DELETE_BOOKING_FIELD_ENDPOINT.replace("{companyId}", companyEntityId).replace("{bookingFieldId}", bookingFieldId),
      method: "DELETE",
      spinner: "deleteActionSpinner"
    });
    if (response.success) {
      setNotification(
        "success",
        <FormattedMessage id="notifications.bookingFields.remove.successTitle"/>,
        <FormattedMessage id="notifications.bookingFields.remove.successText"/>
      );
      return true;
    }
    setNotification(
      "error",
      <FormattedMessage id="notifications.bookingFields.errorTitle" />,
      <FormattedMessage id="notifications.bookingFields.errorText" />
    )
    return false;
  }

  const adminOverrideDiscount = async (payload, companyEntityId) => {
    const response = await  genericApiFetch({
      url: ADMIN_OVERRIDE_DISCOUNT_ENDPOINT.replace("{providerCode}", providerCode?.toLowerCase()).replace("{companyEntityId}", companyEntityId),
      method: "POST",
      data: payload,
      spinner: "overrideDiscountSpinner"
    })
    if (response.success) {
      if (response.data.status === "SUCCESS") {
        setNotification(
          "success",
          <FormattedMessage id="notifications.adminOverrideDiscount.succesTitle"/>,
          <FormattedMessage id="notifications.adminOverrideDiscount.succesText"/>
        )
      }
      return response.data
    }
    setNotification(
      "error",
      <FormattedMessage id="notifications.adminOverrideDiscount.errorTitle" />,
      <FormattedMessage id="notifications.adminOverrideDiscount.errorText" />
    )
    return false
  };

  const getMembershipOptions = async (options) => {
    const response = await genericApiFetch({
      url: GET_OPTIONS_ENDPOINT,
      queryParams: {
        optionName: options,
      },
      method: 'GET',
    });

    if (response.success) {
      return response?.data?.options;
    }
    setNotification(
      'error',
      <FormattedMessage id="notifications.genericTitle" />,
      <FormattedMessage id="notifications.genericDescription" />,
    );
    return false;
  };

  const getGridRowsCount = async () => {
    const response = await genericApiFetch({
      url: GRID_ROWS_COUNT_ENDPOINT,
      method: "GET",
    });

    if (response.success) {
      return response.data;
    }
    return false;
  }

  const dismissTripCreditCompanyBanner = async (companyEntityId, data) => {
    const response = await genericApiFetch({
      data: data,
      url: DISMISS_TRIP_CREDITS_BANNER.replace("{companyId}", companyEntityId),
      method: "POST",
    });

    if (response.success) {
      return response.data;
    }
    return false;
  }

  return {
    companiesResponse,
    activityResponse,
    activityLoading,
    activityFailed,
    getCompaniesList,
    searchCompanies,
    getCompanyBanner,
    getCompanyDetails,
    updateCompany,
    deleteCompany,
    getCompanyAdmins,
    getDomains,
    resendVerification,
    removeDomain,
    addDomain,
    getCompanyActivity,
    setOverrideThreshold,
    updateCompanyCitiPCAN,
    getBookingFields,
    addEditBookingField,
    removeBookingField,
    adminOverrideDiscount,
    getMembershipOptions,
    getGridRowsCount,
    dismissTripCreditCompanyBanner
  };
}

